<template>
    <Modal
        ref="modal"
        :title="(adjustment.id ? 'Update' : 'Create') + ' Scenario Adjustment'"
        :loading="isLoading"
        size="is-large"
        @closed="close"
    >
        <FormField label="Scenario" class="tw-mb-2">
            <Select v-model="adjustment.scenario_id" placeholder="Select a Scenario" expanded>
                <option v-for="option in availableScenarios" :key="'scenarios_option_' + option.id" :value="option.id">
                    {{ option.name }}
                </option>
            </Select>
        </FormField>

        <FormField label="Adjustment" field-class="tw-border tw-p-3 tw-rounded-md">
            <div class="tw-flex tw-gap-2 tw-mb-2">
                <Button
                    :active="adjustment.adjustment_type === 'default'"
                    @click="adjustment.adjustment_type = 'default'"
                >
                    Nominal Value
                </Button>
                <Button
                    :active="adjustment.adjustment_type === 'percentage'"
                    @click="adjustment.adjustment_type = 'percentage'"
                >
                    Percentage
                </Button>
            </div>

            <div class="tw-flex tw-gap-2">
                <FormField label="Current Value">
                    <Currency :model-value="entry.budgeted_cost" disabled />
                </FormField>
                <FormField label="Adjustment Value">
                    <Currency
                        v-model="adjustment.value"
                        :currency="adjustment.adjustment_type === 'percentage' ? '%' : undefined"
                    />
                </FormField>
                <FormField label="Scenario Value">
                    <Currency
                        :model-value="
                            adjustment.adjustment_type === 'percentage'
                                ? parseFloat(entry.budgeted_cost) +
                                  parseFloat(entry.budgeted_cost) * (parseFloat(adjustment.value) / 100)
                                : parseFloat(entry.budgeted_cost) +
                                  parseFloat(isNaN(adjustment.value) ? '0' : adjustment.value)
                        "
                        disabled
                    />
                </FormField>
            </div>
        </FormField>

        <FormField v-if="adjustment.user" label="Created By">
            <User :user="adjustment.user" :hide-name="false" />
        </FormField>

        <template #footer>
            <Button label="Cancel" class="!tw-mr-auto" @click="close">Cancel</Button>
            <Button v-if="adjustment?.id" :disabled="isLoading" type="is-danger" @click="deleteAdjustment">
                Delete
            </Button>
            <Button :disabled="isLoading || !isValid" class="tw-ml-2" type="is-primary" @click="saveAdjustment">
                {{ adjustment?.id ? 'Update' : 'Create' }}
            </Button>
        </template>
    </Modal>
</template>
<script setup>
import { useDataStore } from '@/js/stores/DataStore.js';
import Modal from '@/js/components/Modal.vue';
import Button from '@/js/components/controls/Button.vue';
import FormField from '@/js/components/widgets/FormField.vue';
import { toast } from '@/js/utils.js';
import Select from '@/js/components/controls/Select.vue';
import Currency from '@/js/components/controls/Currency.vue';
import User from '@/js/components/widgets/User.vue';
import { ref, computed, useTemplateRef } from 'vue';

const props = defineProps({
    entry: {
        type: Object,
        default() {
            return {};
        },
    },
    adjustments: {
        type: Array,
        default() {
            return [];
        },
    },
});
const store = useDataStore();
const isLoading = ref(false);
const scenarios = ref(store.get('current_event.scenarios'));
const adjustment = ref({
    adjustment_type: 'default',
    value: 0,
    scenario_id: null,
});
const modal = useTemplateRef('modal');
const availableScenarios = computed(() => {
    const scenarioIds =
        props.adjustments.map((adj) => {
            return adj.scenario_id;
        }) ?? [];

    return Object.values(scenarios.value).filter(
        (scenario) => adjustment.value.scenario_id === scenario.id || !scenarioIds.includes(scenario.id)
    );
});
const isValid = computed(() => {
    return adjustment.value.scenario_id && adjustment.value.value && adjustment.value.adjustment_type;
});
const open = (adj = null) => {
    if (adj) {
        adjustment.value = adj;
    }
    if (adjustment.value && !adjustment.value.scenario_id) {
        const available_scenario = availableScenarios.value.length === 1 ? availableScenarios.value[0].id : null;

        adjustment.value.scenario_id = store.get('current_scenario')
            ? store.get('current_scenario')
            : available_scenario;
    }

    modal.value.open();
};
const close = () => {
    adjustment.value = {
        adjustment_type: 'default',
        value: 0,
        scenario_id: null,
    };
    isLoading.value = false;
    modal.value.close();
};
const saveAdjustment = () => {
    isLoading.value = true;
    axios({
        method: adjustment.value.id ? 'patch' : 'post',
        url: adjustment.value.id
            ? route('api.account.event.entries.adjustments.update', {
                  entry: props.entry,
                  adjustment: adjustment.value.id,
              })
            : route('api.account.event.entries.adjustments.create', {
                  entry: props.entry,
              }),
        data: {
            scenario_id: adjustment.value.scenario_id,
            adjustment_type: adjustment.value.adjustment_type,
            value: adjustment.value.value,
        },
    })
        .then(() => {
            toast(
                'Success',
                `Your scenario adjustment has been ${adjustment.value.id ? 'updated' : 'created'}`,
                'success'
            );
            Eventbus.$emit('reload:adjustmentsTab');
            Eventbus.$emit('reload:entries');
            Eventbus.$emit('reload:charts');
            Eventbus.$emit('reload:scenariosTable');
            Eventbus.$emit('scenarioAdjustments:reload');
            close();
        })
        .catch(() => {
            isLoading.value = false;
        });
};
const deleteAdjustment = () => {
    axios
        .delete(
            route('api.account.event.entries.adjustments.delete', {
                entry: props.entry,
                adjustment: adjustment.value.id,
            })
        )
        .then(() => {
            toast('Success', 'Your scenario adjustment has been deleted', 'success');
            Eventbus.$emit('reload:adjustmentsTab');
            Eventbus.$emit('reload:entries');
            Eventbus.$emit('reload:charts');
            Eventbus.$emit('scenarioAdjustments:reload');
            close();
        });
};

defineExpose({
    open,
});
</script>
